
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface CharacterAgeGroup {
  id: string;
  name: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useCharacterAgeGroups = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: ageGroups = [], isLoading, error } = useQuery({
    queryKey: ['character-age-groups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_age_groups')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as CharacterAgeGroup[];
    },
  });

  const createAgeGroup = useMutation({
    mutationFn: async (name: string) => {
      const maxOrder = Math.max(...ageGroups.map(ag => ag.display_order), -1);
      const { data, error } = await supabase
        .from('character_age_groups')
        .insert([{ 
          name, 
          display_order: maxOrder + 1,
          is_active: true 
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-age-groups'] });
      toast({
        title: "Success",
        description: "Age group created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create age group",
        variant: "destructive",
      });
    },
  });

  const updateAgeGroup = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CharacterAgeGroup> }) => {
      const { data, error } = await supabase
        .from('character_age_groups')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-age-groups'] });
      toast({
        title: "Success",
        description: "Age group updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update age group",
        variant: "destructive",
      });
    },
  });

  const deleteAgeGroup = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('character_age_groups')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-age-groups'] });
      toast({
        title: "Success",
        description: "Age group deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete age group",
        variant: "destructive",
      });
    },
  });

  return {
    ageGroups,
    isLoading,
    error,
    createAgeGroup,
    updateAgeGroup,
    deleteAgeGroup,
  };
};
